/* SpawnAnything — "Grafik: Automatisch": keeps the frame rate by giving up effects, never resolution (the game
   always renders at the display's native pixel ratio, so it stays sharp).
   Degrade ladder (one step per second while frames are late): tilt-shift blur → shadow maps (→ blob shadows) →
   crease outlines (silhouettes stay; halves the outline pass) → shorter mesh / impostor distances → grass →
   fewer debris particles → fine ground detail (0.5 m sub-voxel colours, src/world/groundDetail.js) → shorter mesh distances (no sprite
   impostors any more: far units stay animated 3D models).
   Upgrades are probed: after 5 s at the target rate one step is given back; a step that fails again is blocked for
   longer each time (20 s, 40 s, 80 s …) so the setting does not oscillate.
   The target frame time is the display refresh (shortest interval seen) or the frame cap, whichever is longer. */

export const AUTO_STEPS = ['tilt', 'shadows', 'creases', 'lod', 'grass', 'fx', 'ground', 'lod2'];

/** @param {(level: number) => void} apply  sets the game to `level` (0 = everything on, AUTO_STEPS.length = all steps taken) */
export function createAutoQuality(apply) {
  let level = 0, avg = 16.7, refresh = 1000, refreshT = 0, prev = 0, checkT = 0, goodSince = 0, graceUntil = 0;
  const blockedUntil = new Array(AUTO_STEPS.length + 1).fill(0), fails = new Array(AUTO_STEPS.length + 1).fill(0);
  return {
    get level() { return level; },
    reset() { level = 0; avg = 16.7; prev = 0; goodSince = 0; graceUntil = 0; blockedUntil.fill(0); fails.fill(0); apply(0); },
    /** ignore the next ms (army rebuild, shader compiles) */
    hold(ms = 3000) { graceUntil = performance.now() + ms; prev = 0; },
    /** call once per drawn frame; capMs = frame cap interval (0 = none) */
    tick(now, capMs = 0) {
      if (prev) {
        const dt = now - prev;
        if (dt < 120) avg += (dt - avg) * 0.05; // ignore tab switches / one-off hitches
        if (dt < refresh) refresh = Math.max(4, dt);
      }
      prev = now;
      if (!graceUntil) graceUntil = now + 3000; // start-up: shader compiles, first uploads
      if (now < graceUntil) { avg = Math.min(avg, 16.7); return; }
      if (now - refreshT > 10000) { refreshT = now; refresh = Math.min(refresh * 1.2, 1000); } // re-learn slowly (monitor change)
      if (now - checkT < 1000) return;
      checkT = now;
      // snap to a real display period (144/120/90/60/30 Hz) so one early rAF does not set an impossible target
      const hz = [6.94, 8.33, 11.11, 16.67, 33.33].find(p => p >= refresh * 0.92) || 33.33;
      const target = Math.max(hz, capMs || 0);
      if (avg > target * 1.3 + 1.5 && level < AUTO_STEPS.length) {
        level++; apply(level); goodSince = 0;
        fails[level - 1]++; blockedUntil[level - 1] = now + 20000 * Math.pow(2, Math.min(5, fails[level - 1] - 1));
        avg = target; // give the new setting a fresh start
      } else if (avg <= target * 1.08 + 0.5) {
        if (!goodSince) goodSince = now;
        if (level > 0 && now - goodSince > 5000 && now > blockedUntil[level - 1]) { level--; apply(level); goodSince = now; }
      } else goodSince = 0;
    },
  };
}
